import { createSelector } from '@reduxjs/toolkit';
import type { WARootState } from '@/store/store';
import { convertToNavigationItem } from '@/config/navigations';
import type { NavigationItem } from '@/config/navigations';
import { applyMenuPatches } from '@/utils/navigationPatches';
import {
  filterNavItemsForAdvisor,
  finalizeAdvisorClientsSection,
  isAdvisorRouteHidden,
} from '@/modules/advisor/utils/advisorNavigation';
import { normalizePath } from '@/utils/appMenuFilter';
import { MANAGED_ACCOUNTS_INTERNAL_ROUTES } from '@/modules/advisor/managedAccounts/utils/managedAccountsRoutes';
import { paths } from '@/constants/paths';
import { Role } from '@/models/AccountEnums';
import type { AppUserInfo } from '@/models/AppViewModel';

/**
 * Full Advisor nav pipeline (convert → patch → advisor hiding), shared by the
 * sidebar, CommandPalette and SubNavigationTabRow via `selectAdvisorNavigation`
 * so every consumer renders the same tree.
 *
 * Returns an UNTRANSLATED tree — selectors can't call the `t()` hook, so each
 * consumer applies its own translation pass. Icons here are only the
 * backend-driven `InvestorIconMap` ones; the sidebar-only Configuration /
 * Client's Wealth icons stay in the sidebar (they apply to all roles there).
 */
export function buildAdvisorNavigation(
  userInfo: AppUserInfo,
): NavigationItem[] {
  const converted = (userInfo.Menu ?? []).map((item) =>
    convertToNavigationItem(item, Role.Advisor),
  );
  const patched = applyMenuPatches(converted, Role.Advisor, { userInfo });
  // Clients section shape (Relationships dropdown vs plain Clients leaf) is
  // decided here, after hiding, based on whether any real sub-page survived.
  return finalizeAdvisorClientsSection(filterNavItemsForAdvisor(patched));
}

const selectUserInfo = (state: WARootState) => state.appView.UserInfo;
const selectCurrentRole = (state: WARootState) => state.auth.currentRole;

/**
 * Memoized Advisor navigation tree — the single source of truth for what an
 * advisor can navigate to. The backend menu is authoritative for the Advisor
 * role only, so this selector is advisor-scoped: it returns `undefined` when
 * the active role is not Advisor or the menu hasn't loaded, and consumers fall
 * through to their existing per-role pipelines.
 *
 * Recomputes only when `UserInfo` or `currentRole` changes; `UserInfo` is
 * replaced wholesale on each `/App/{role}` fetch (including role switches and
 * impersonation), so the result can never go stale.
 */
export const selectAdvisorNavigation = createSelector(
  [selectUserInfo, selectCurrentRole],
  (userInfo, currentRole): NavigationItem[] | undefined => {
    if (currentRole !== Role.Advisor || !userInfo?.Menu) return undefined;
    return buildAdvisorNavigation(userInfo);
  },
);

/**
 * Route permission set derived from the advisor nav tree (normalized:
 * lowercase, no trailing slash).
 *
 * - `all`: every route in the tree — the exact set of navigable targets. A
 *   route is permitted iff it appears here; there is no ancestor/wildcard
 *   inheritance, so a page the backend didn't grant is never implicitly
 *   reachable. (Advisor has no dynamic `:param` routes; dynamic selection —
 *   client, account, document — travels in a query param, which `normalizePath`
 *   strips, so the pathname is always a route the backend granted.)
 */
export interface AdvisorAllowedRoutes {
  all: ReadonlySet<string>;
}

/**
 * True when a section parent's route is a child's route verbatim (the
 * self-referential index child) or an ancestor of one (a real section index).
 * A parent whose route is neither aliases a SIBLING leaf, and granting it would
 * permit a page the backend never sent as a child.
 */
const parentOwnsItsRoute = (
  parentRoute: string,
  children: NavigationItem[],
): boolean =>
  children.some((child) => {
    const childRoute = child.route ? normalizePath(child.route) : '';
    return (
      childRoute === parentRoute || childRoute.startsWith(`${parentRoute}/`)
    );
  });

const collectRoutes = (nodes: NavigationItem[], all: Set<string>): void => {
  for (const node of nodes) {
    const normalized = node.route ? normalizePath(node.route) : '';
    const children = node.children ?? [];
    if (
      normalized &&
      (children.length === 0 || parentOwnsItsRoute(normalized, children))
    ) {
      all.add(normalized);
    }
    if (children.length > 0) collectRoutes(children, all);
  }
};

/**
 * Memoized permission set for the advisor nav artifact. `undefined` exactly
 * when `selectAdvisorNavigation` is `undefined` — callers fall back to the
 * legacy `canAccessRoute` path for other roles or an unloaded menu.
 */
export const selectAdvisorAllowedRoutes = createSelector(
  [selectAdvisorNavigation],
  (tree): AdvisorAllowedRoutes | undefined => {
    if (!tree) return undefined;
    const all = new Set<string>();
    collectRoutes(tree, all);
    return { all };
  },
);

/**
 * Advisor route permission derived from the nav artifact, so what is rendered
 * as clickable and what the guard allows come from the same object.
 *
 * Routes are permitted only when they are exact members of the nav artifact.
 * Query parameters are intentionally absent from `pathname`, so URL state can
 * reuse an exact route grant without widening path permissions. Order:
 * 1. Hidden routes are denied outright.
 * 2. The role index always renders the dashboard (parity with
 *    `validateByUrl`'s root allowance).
 * 3. An empty artifact fails closed: only the dashboard stays reachable.
 * 4. Exact membership against every tree route.
 *
 * `pathname` is the full app path (role prefix included), as from
 * `location.pathname`.
 */
export function canAccessAdvisorRoute(
  pathname: string,
  allowedRoutes: AdvisorAllowedRoutes,
): boolean {
  if (isAdvisorRouteHidden(pathname)) return false;
  const normalized = normalizePath(pathname);
  // normalizePath('/') === '' — the role index renders the dashboard.
  if (normalized === '/advisor' || normalized === '') return true;
  if (allowedRoutes.all.size === 0) return normalized === '/advisor/dashboard';
  if (allowedRoutes.all.has(normalized)) return true;
  // Managed Accounts is a single granted feature whose internal tab routes and
  // the Assign & Edit Accounts page live under the section index but aren't
  // separate nav items. Allow exactly those when the section itself is granted
  // — never a prefix/wildcard, which would also admit unknown future subpaths
  // (AGENTS.md: permission is exact set membership, no ancestor inheritance).
  const managedAccounts = normalizePath(
    paths.advisor.configuration.managedAccounts.index,
  );
  return (
    allowedRoutes.all.has(managedAccounts) &&
    MANAGED_ACCOUNTS_INTERNAL_ROUTES.has(normalized)
  );
}
